/* ------------------------------------------------------------------------------
 *
 *  # Calendario de Visitas Leads - FullCalendar Integration
 *
 *  Gestión de calendario de visitas de leads con FullCalendar
 *
 * ---------------------------------------------------------------------------- */

// Variables de estado
let calendarioLeadsCallbacks = null;
let calendarLeadsInstance = null;
let currentLeadsView = 'dayGridMonth';

// Configuración de colores por estado
const ESTADO_COLORS = {
	'Nuevo': '#6c757d',
	'Contactado': '#0d6efd',
	'Visita Programada': '#ffc107',
	'Visitado': '#17a2b8',
	'Admitido': '#28a745',
	'No admitido': '#dc3545',
	'No interesado': '#FFA500',
	'Interesado': '#6A5ACD'
};

// Helper functions
function getEventColorByEstado(estadoNombre) {
	return ESTADO_COLORS[estadoNombre] || '#6c757d';
}

function getEventClassNamesByEstado(estadoNombre) {
	const classes = ['fc-event-visita'];
	
	switch(estadoNombre) {
		case 'Admitido':
			classes.push('fc-event-admitido');
			break;
		case 'No admitido':
			classes.push('fc-event-rechazado');
			break;
		case 'Visita Programada':
			classes.push('fc-event-programada');
			break;
		default:
			classes.push('fc-event-pendiente');
	}
	
	return classes;
}

// FullCalendar initialization for Leads
export function initCalendarioLeads(containerId, visitas = [], dotNetRef = null) {
	if (typeof FullCalendar == 'undefined') {
		console.error('FullCalendar library is not loaded.');
		return null;
	}

	const calendarEl = document.getElementById(containerId);
	if (!calendarEl) {
		console.warn(`Calendar container #${containerId} not found`);
		return null;
	}

	// Destroy previous instance
	if (calendarLeadsInstance) {
		calendarLeadsInstance.destroy();
		calendarLeadsInstance = null;
	}

	// Convertir visitas al formato de FullCalendar
	const calendarEvents = visitas.map(v => ({
		id: v.id.toString(),
		title: v.title,
		start: v.start,
		end: v.end,
		allDay: v.allDay || false,
		color: v.color || getEventColorByEstado(v.estadoNombre),
		extendedProps: v.extendedProps || {}
	}));

	console.log('Initializing calendar with', calendarEvents.length, 'visitas');

	try {
		calendarLeadsInstance = new FullCalendar.Calendar(calendarEl, {
			headerToolbar: {
				left: 'prev,next today',
				center: 'title',
				right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
			},
			initialView: currentLeadsView,
			locale: 'es',
			buttonText: {
				today: 'Hoy',
				month: 'Mes',
				week: 'Semana',
				day: 'Día',
				list: 'Lista'
			},
			navLinks: true,
			selectable: true,
			selectMirror: true,
			editable: false,
			dayMaxEvents: true,
			weekNumbers: true,
			weekText: 'Sem',
			events: calendarEvents,
			slotMinTime: '08:00:00',
			slotMaxTime: '20:00:00',

			// Event rendering
			eventContent: function (arg) {
				const numeroVisita = arg.event.extendedProps.numeroVisita || '';
				const parentName = arg.event.extendedProps.parentName || '';
				const childName = arg.event.extendedProps.childName || '';
				
				return {
					html: `<div class="fc-event-main-frame">
						<div class="fc-event-time">${arg.timeText}</div>
						<div class="fc-event-title-container">
							<div class="fc-event-title fc-sticky">
								<strong>V${numeroVisita}:</strong> ${parentName}
								${childName ? `<br><small class="text-white-50">${childName}</small>` : ''}
							</div>
						</div>
					   </div>`
				};
			},

			eventClassNames: function (arg) {
				const estadoNombre = arg.event.extendedProps.estadoNombre || 'Nuevo';
				return getEventClassNamesByEstado(estadoNombre);
			},

			// Event click
			eventClick: function (info) {
				info.jsEvent.preventDefault();
				console.log('Visita clicked:', info.event.id, info.event.extendedProps);

				if (dotNetRef) {
					const leadId = parseInt(info.event.extendedProps.leadId);
					const numeroVisita = parseInt(info.event.extendedProps.numeroVisita) || 1;
					dotNetRef.invokeMethodAsync('OnVisitaClick', leadId, numeroVisita);
				}
			},

			// Date select (programar nueva visita)
			select: function (info) {
				console.log('Date selected:', info.startStr, 'to', info.endStr);

				if (dotNetRef) {
					dotNetRef.invokeMethodAsync('OnDateSelect',
						info.startStr,
						info.allDay
					);
				}

				// Deselect
				calendarLeadsInstance.unselect();
			},

			// View change
			datesSet: function (info) {
				currentLeadsView = info.view.type;
				console.log('View changed to:', currentLeadsView);

				// Recargar visitas del rango visible
				if (dotNetRef) {
					dotNetRef.invokeMethodAsync('OnViewChange',
						info.startStr,
						info.endStr,
						currentLeadsView
					);
				}
			},

			// Loading
			loading: function (isLoading) {
				if (isLoading) {
					window.sfBlazorSB.toggleSpinner(true);
				} else {
					window.sfBlazorSB.toggleSpinner(false);
				}
			}
		});

		calendarLeadsInstance.render();
		console.log('Calendario Leads initialized successfully');
		
		return calendarLeadsInstance;
	} catch (e) {
		console.error('FullCalendar Leads error:', e);
		return null;
	}
}

// Add visita to calendar
export function addVisitaToCalendar(visita) {
	if (!calendarLeadsInstance) {
		console.warn('Calendar Leads not initialized');
		return;
	}

	const calendarEvent = {
		id: visita.id.toString(),
		title: visita.title,
		start: visita.start,
		end: visita.end,
		allDay: visita.allDay || false,
		color: visita.color || getEventColorByEstado(visita.estadoNombre),
		extendedProps: visita.extendedProps || {}
	};

	calendarLeadsInstance.addEvent(calendarEvent);
	console.log('Visita added to calendar:', visita.id);
}

// Update visita in calendar
export function updateVisitaInCalendar(visita) {
	if (!calendarLeadsInstance) {
		console.warn('Calendar Leads not initialized');
		return;
	}

	const existingEvent = calendarLeadsInstance.getEventById(visita.id.toString());
	if (existingEvent) {
		existingEvent.setProp('title', visita.title);
		existingEvent.setStart(visita.start);
		existingEvent.setEnd(visita.end);
		existingEvent.setProp('allDay', visita.allDay || false);
		existingEvent.setProp('color', visita.color || getEventColorByEstado(visita.estadoNombre));
		existingEvent.setExtendedProp('extendedProps', visita.extendedProps || {});
		
		console.log('Visita updated in calendar:', visita.id);
	}
}

// Remove visita from calendar
export function removeVisitaFromCalendar(visitaId) {
	if (!calendarLeadsInstance) {
		console.warn('Calendar Leads not initialized');
		return;
	}

	const event = calendarLeadsInstance.getEventById(visitaId.toString());
	if (event) {
		event.remove();
		console.log('Visita removed from calendar:', visitaId);
	}
}

// Refresh all visitas
export function refreshCalendarLeadsVisitas(visitas) {
	if (!calendarLeadsInstance) {
		console.warn('Calendar Leads not initialized');
		return;
	}

	// Remove all events
	calendarLeadsInstance.getEvents().forEach(event => event.remove());
	
	// Add new events
	visitas.forEach(v => {
		const calendarEvent = {
			id: v.id.toString(),
			title: v.title,
			start: v.start,
			end: v.end,
			allDay: v.allDay || false,
			color: v.color || getEventColorByEstado(v.estadoNombre),
			extendedProps: v.extendedProps || {}
		};
		calendarLeadsInstance.addEvent(calendarEvent);
	});
	
	console.log('Calendar Leads refreshed with', visitas.length, 'visitas');
}

// Go to date
export function gotoDateLeads(dateStr) {
	if (!calendarLeadsInstance) {
		console.warn('Calendar Leads not initialized');
		return;
	}

	calendarLeadsInstance.gotoDate(dateStr);
	console.log('Calendar Leads navigated to:', dateStr);
}

// Change view
export function changeViewLeads(viewType) {
	if (!calendarLeadsInstance) {
		console.warn('Calendar Leads not initialized');
		return;
	}

	calendarLeadsInstance.changeView(viewType);
	currentLeadsView = viewType;
	console.log('Calendar Leads view changed to:', viewType);
}

// Get current view info
export function getCurrentLeadsViewInfo() {
	if (!calendarLeadsInstance) {
		return null;
	}

	const view = calendarLeadsInstance.view;
	return {
		type: view.type,
		title: view.title,
		currentStart: view.currentStart.toISOString(),
		currentEnd: view.currentEnd.toISOString()
	};
}

// Destroy calendar
export function destroyCalendarioLeads() {
	if (calendarLeadsInstance) {
		calendarLeadsInstance.destroy();
		calendarLeadsInstance = null;
		console.log('Calendar Leads destroyed');
	}
}

// Callbacks
export function setCalendarioLeadsCallbacks(dotNetRef) {
	calendarioLeadsCallbacks = dotNetRef;
}

// Filters
export function applyCalendarioLeadsFilters() {
	const estadoFilter = document.getElementById('estadoFilterCalendar')?.value || '';
	const ambienteFilter = document.getElementById('ambienteFilterCalendar')?.value || '';
	const soloSinProgramar = document.getElementById('soloSinProgramarFilter')?.checked || false;
	
	if (calendarioLeadsCallbacks) {
		calendarioLeadsCallbacks.invokeMethodAsync('ApplyFilters', 
			estadoFilter, 
			ambienteFilter, 
			soloSinProgramar
		);
	}
}

export function clearCalendarioLeadsFilters() {
	const estadoFilter = document.getElementById('estadoFilterCalendar');
	const ambienteFilter = document.getElementById('ambienteFilterCalendar');
	const soloSinProgramar = document.getElementById('soloSinProgramarFilter');
	
	if (estadoFilter) estadoFilter.value = '';
	if (ambienteFilter) ambienteFilter.value = '';
	if (soloSinProgramar) soloSinProgramar.checked = false;
	
	if (calendarioLeadsCallbacks) {
		calendarioLeadsCallbacks.invokeMethodAsync('ClearFilters');
	}
}
